"use client"

import * as React from "react"

type ThemeMode = "light" | "dark" | "system"

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: ThemeMode
  enableSystem?: boolean
  attribute?: "class" | string
  disableTransitionOnChange?: boolean
  forcedTheme?: ThemeMode
}

type ThemeContextValue = {
  theme: ThemeMode
  resolvedTheme: "light" | "dark"
  systemTheme: "light" | "dark"
  setTheme: (next: string) => void
}

const STORAGE_KEY = "theme"
const ThemeContext = React.createContext<ThemeContextValue | null>(null)

function resolveSystemTheme(): "light" | "dark" {
  if (typeof window === "undefined") return "dark"
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light"
}

function disableThemeTransitionsTemporarily(): (() => void) | null {
  if (typeof window === "undefined") return null
  const style = document.createElement("style")
  style.appendChild(
    document.createTextNode(
      "*,*::before,*::after{transition:none!important;animation:none!important}",
    ),
  )
  document.head.appendChild(style)
  return () => {
    window.getComputedStyle(document.body)
    window.setTimeout(() => {
      style.remove()
    }, 1)
  }
}

function applyThemeAttribute(
  attribute: string,
  theme: ThemeMode,
  enableSystem: boolean,
): "light" | "dark" {
  const resolved =
    theme === "system" && enableSystem ? resolveSystemTheme() : theme
  const resolvedTheme = (resolved === "dark" ? "dark" : "light") as
    | "light"
    | "dark"

  const root = document.documentElement
  if (attribute === "class") {
    root.classList.remove("light", "dark")
    root.classList.add(resolvedTheme)
  } else {
    root.setAttribute(attribute, resolvedTheme)
  }
  root.style.colorScheme = resolvedTheme
  return resolvedTheme
}

function parseThemeMode(input: string): ThemeMode {
  if (input === "light" || input === "dark" || input === "system") {
    return input
  }
  return "system"
}

export function ThemeProvider({
  children,
  defaultTheme = "system",
  enableSystem = true,
  attribute = "class",
  disableTransitionOnChange = false,
  forcedTheme,
}: ThemeProviderProps) {
  const [theme, setThemeState] = React.useState<ThemeMode>(defaultTheme)
  const [systemTheme, setSystemTheme] = React.useState<"light" | "dark">("dark")
  const [resolvedTheme, setResolvedTheme] = React.useState<"light" | "dark">("dark")

  React.useEffect(() => {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    const next: ThemeMode = parseThemeMode(stored ?? defaultTheme)
    setThemeState(next)
    const system = resolveSystemTheme()
    setSystemTheme(system)
    setResolvedTheme(
      applyThemeAttribute(attribute, forcedTheme ?? next, enableSystem),
    )
  }, [attribute, defaultTheme, enableSystem, forcedTheme])

  React.useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)")
    const onChange = () => {
      const nextSystem = media.matches ? "dark" : "light"
      setSystemTheme(nextSystem)
      setResolvedTheme((prev) => {
        if (forcedTheme) {
          return applyThemeAttribute(attribute, forcedTheme, enableSystem)
        }
        if (theme !== "system" || !enableSystem) return prev
        return applyThemeAttribute(attribute, "system", enableSystem)
      })
    }
    media.addEventListener("change", onChange)
    return () => media.removeEventListener("change", onChange)
  }, [attribute, enableSystem, forcedTheme, theme])

  React.useEffect(() => {
    if (!forcedTheme) return
    setResolvedTheme(applyThemeAttribute(attribute, forcedTheme, enableSystem))
  }, [attribute, enableSystem, forcedTheme])

  const setTheme = React.useCallback(
    (nextRaw: string) => {
      const next = parseThemeMode(nextRaw)
      const cleanup = disableTransitionOnChange
        ? disableThemeTransitionsTemporarily()
        : null
      setThemeState(next)
      window.localStorage.setItem(STORAGE_KEY, next)
      setResolvedTheme(
        applyThemeAttribute(attribute, forcedTheme ?? next, enableSystem),
      )
      cleanup?.()
    },
    [attribute, disableTransitionOnChange, enableSystem, forcedTheme],
  )

  const value = React.useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme, systemTheme, setTheme }),
    [theme, resolvedTheme, systemTheme, setTheme],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const ctx = React.useContext(ThemeContext)
  if (!ctx) {
    return {
      theme: "system",
      resolvedTheme: "dark",
      systemTheme: "dark",
      setTheme: () => {},
    }
  }
  return ctx
}
